/**
 * Google Analytics 4 utility module.
 *
 * Manages GA4 initialization with Consent Mode v2 (default deny)
 * and provides a consent grant function for the cookie banner.
 *
 * Uses dynamic script injection so VITE_GA_MEASUREMENT_ID works
 * natively via import.meta.env. If the env var is empty/undefined,
 * all functions are no-ops (safe for local development).
 */

/**
 * Initialize Google Analytics 4 with Consent Mode v2.
 *
 * - Creates window.dataLayer and window.gtag
 * - Sets consent defaults to 'denied' (GDPR compliant)
 * - Injects the gtag.js script from Google CDN
 * - Configures GA4 with the measurement ID
 *
 * Does nothing if VITE_GA_MEASUREMENT_ID is not set.
 */
export function initAnalytics() {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;

  if (!measurementId) {
    return;
  }

  // Guard against multiple initializations (e.g., HMR in development)
  if (window.gtag) {
    return;
  }

  // Validate GA4 measurement ID format (G- prefix followed by alphanumeric)
  if (!/^G-[A-Z0-9]+$/i.test(measurementId)) {
    console.error('Invalid GA4 measurement ID format:', measurementId);
    return;
  }

  // Create dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  // Set consent defaults to denied (GDPR Consent Mode v2)
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  // Inject preconnect hint for GTM (only when GA is actually used)
  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = 'https://www.googletagmanager.com';
  preconnect.crossOrigin = '';
  document.head.appendChild(preconnect);

  // Inject gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Configure GA4
  gtag('js', new Date());
  gtag('config', measurementId);
}

/**
 * Grant analytics consent.
 *
 * Call this when the user accepts cookies in the consent banner.
 * Updates Consent Mode v2 to allow analytics storage (cookies).
 *
 * Safe to call even if GA4 was not initialized (no-op).
 */
export function grantConsent() {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
  }
}

/**
 * Revoke analytics consent.
 *
 * Call this when the user withdraws cookie consent.
 * Updates Consent Mode v2 to deny analytics storage and
 * deletes existing GA cookies (_ga, _ga_*).
 *
 * Safe to call even if GA4 was not initialized (no-op).
 */
export function revokeConsent() {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
    });
  }
  // Delete GA cookies from all possible paths and domains
  const cookies = document.cookie.split(';');
  const hostname = window.location.hostname;
  const bareDomain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
  const dotRootDomain = `.${bareDomain}`;
  for (const cookie of cookies) {
    const name = cookie.split('=')[0].trim();
    if (name === '_ga' || name.startsWith('_ga_')) {
      // Host-only domain
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${hostname}`;
      // No domain attribute
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      // Root domain with leading dot to cover subdomains (e.g. .explainit.hu)
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${dotRootDomain}`;
    }
  }
}

/**
 * Track a custom GA4 event.
 *
 * @param {string} eventName - GA4 event name (e.g., 'menu_open', 'menu_item_click')
 * @param {Object} [params={}] - Optional event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}

/**
 * Track hamburger menu open.
 */
export function trackMenuOpen() {
  trackEvent('menu_open');
}

/**
 * Track hamburger menu item click.
 * @param {string} label - Menu item label (e.g., 'Main page', 'Blog')
 */
export function trackMenuItemClick(label) {
  trackEvent('menu_item_click', { item_label: label });
}

/**
 * Track blog page view.
 * @param {string} [slug] - Article slug for individual article pages
 */
export function trackBlogPageView(slug) {
  trackEvent('blog_page_view', slug ? { article_slug: slug } : {});
}

/**
 * Track article link click from blog listing.
 * @param {string} slug - Article slug
 */
export function trackArticleLinkClick(slug) {
  trackEvent('article_link_click', { article_slug: slug });
}
